import { streamSse } from '@nyce/admin-kit/api';
import { targetHubClient } from '../../identity/targetClient';

/**
 * nyce-logic-automation-gw through the hub (`/automation/<target>/…`, `AutomationGw/AutomationGwEndpoints.cs`). The hub
 * calls the gateway with the operator's AUTH session of that environment when there is one (sent as `X-Target-Token`),
 * else with its admin key, which the console never sees. Shared secrets are hashed in the gateway: `rotateSecret` issues
 * a new one, shown once. `moveToAuth` gives an agent its own AUTH client over its socket (`docs/automation-gw-auth.md`).
 */

export interface Tenant {
  id: string;
  name: string;
  /** The customer's AUTH tenant (G8); agents of an unlinked tenant cannot move to AUTH. */
  authTenantId?: string | null;
}

/** legacy = shared secret only, both = AUTH token or secret, auth = AUTH token only. */
export type AuthMode = 'legacy' | 'both' | 'auth';
export type Platform = 'windows' | 'linux' | 'container';
export type ConfigSource = 'bootstrapper' | 'current' | 'env';

/** A registered agent (the gateway calls it a client). */
export interface Agent {
  id: string;
  tenantId: string;
  /** What the agent connects with (`AGENT__CLIENTID`). */
  clientId: string;
  displayName: string;
  allowedIpRanges: string[];
  /** Only on create/update/rotate answers (the value the writer set or the gateway generated). */
  sharedSecret?: string | null;
  hasSecret?: boolean;
  /** Last reported worker version, platform and when it was last connected (Q1). */
  lastVersion?: string | null;
  lastPlatform?: Platform | null;
  lastSeenAt?: string | null;
  /** Has a bootstrapper, so new workers (and auth) reach it remotely; null = not known yet. */
  remoteUpdatable?: boolean | null;
  /** Where its config was read from; `current` alone would be lost on an update (Q3). */
  configSource?: ConfigSource | null;
  authMode?: AuthMode;
  authClientId?: string | null;
  lastAuthMethod?: 'secret' | 'bearer' | null;
  lastAuthError?: string | null;
  lastBearerAt?: string | null;
}

export interface ContainerAuthClient {
  agent: Agent;
  authClientId: string;
  clientSecret: string;
  /** `AGENT__AUTH__CLIENTID` / `AGENT__AUTH__CLIENTSECRET` for the container. */
  environment: Record<string, string>;
}

export interface AgentInput {
  tenantId: string;
  clientId: string;
  displayName: string;
  /** Empty = generated on create, unchanged on update. */
  sharedSecret?: string;
  allowedIpRanges: string[];
}

export type RouteDirection = 'forward' | 'reverse';

export interface Route {
  id: string;
  /** The agent's database id. */
  clientId: string;
  publicPort: number;
  targetHost: string;
  targetPort: number;
  enabled: boolean;
  allowedNamespaces: string[];
  direction: RouteDirection;
  allowedSourceIPs: string[];
  channelInactivityTimeoutSeconds?: number | null;
  sendProxyProtocol?: boolean;
}

export type RouteInput = Omit<Route, 'id'>;

export interface RouteStatus {
  routeId: string;
  publicPort: number;
  targetHost: string;
  targetPort: number;
  enabled: boolean;
  bytesToAgent: number;
  bytesFromAgent: number;
  activeChannels: number;
  lastActivity?: string | null;
}

/** One agent's live state, from the gateway pod that holds its tunnel. */
export interface TunnelStatus {
  clientDbId: string;
  clientId: string;
  displayName: string;
  tenantName: string;
  routeCount: number;
  isConnected: boolean;
  activeChannels: number;
  connectedAt?: string | null;
  controlBytesSent: number;
  controlBytesReceived: number;
  fileTransferBytesSent: number;
  fileTransferBytesReceived: number;
  routes: RouteStatus[];
  machineName?: string | null;
}

export interface ConnectionHistoryEntry {
  id: number;
  machineName: string;
  agentVersion?: string | null;
  connectedAt: string;
  disconnectedAt?: string | null;
  disconnectReason?: string | null;
}

export interface DiskInfo {
  name: string;
  label: string;
  totalSizeMB: number;
  availableFreeMB: number;
  driveFormat: string;
}

export interface NetworkInterfaceInfo {
  name: string;
  description: string;
  type: string;
  macAddress: string;
  iPv4Addresses: string[];
  iPv6Addresses: string[];
  speed: number;
}

export interface AgentInfo {
  clientId: string;
  version: string;
  machineName: string;
  osDescription: string;
  osArchitecture: string;
  processArchitecture: string;
  dotNetVersion: string;
  processorCount: number;
  totalMemoryMB: number;
  availableMemoryMB: number;
  workingSetMB: number;
  diskInfo: DiskInfo[];
  networkInterfaces: NetworkInterfaceInfo[];
  tcpListeners: { localAddress: string; localPort: number }[];
  tcpConnections: {
    localAddress: string;
    localPort: number;
    remoteAddress: string;
    remotePort: number;
    state: string;
  }[];
  udpListeners: { localAddress: string; localPort: number }[];
  startTime: string;
  uptime: string;
  receivedAt: string;
}

export interface UpdateInfo {
  version: string;
  releaseDate: string;
  releaseNotes?: string | null;
}

export interface TriggerUpdateResponse {
  clientId: string;
  status: string;
  message?: string | null;
  targetVersion?: string | null;
}

export interface LogStatus {
  clientId: string;
  isConnected: boolean;
  logsEnabled: boolean;
  debugLoggingEnabled: boolean;
}

export interface AgentLogEntry {
  timestamp: unknown;
  level: string;
  category: string;
  message: string;
  exception?: string | null;
}

export interface StatPoint {
  timestamp: string;
  /** Cumulative counters of the route since the gateway pod started. */
  bytesToAgent: number;
  bytesFromAgent: number;
  activeChannels: number;
}

export interface RouteStats {
  routeId: string;
  clientName: string;
  publicPort: number;
  dataPoints: StatPoint[];
}

export type PostAction = 'none' | 'delete' | 'rename' | 'movetosubfolder';
export type ErrorAction = 'retryandmove' | 'retryandleave' | 'skipandlog';

export interface FileWatcher {
  id: string;
  /** The agent's database id. */
  clientId: string;
  name: string;
  smbUri: string;
  username?: string | null;
  hasPassword: boolean;
  fileFilters: string[];
  destinationUrl: string;
  destinationHeaders: Record<string, string>;
  enabled: boolean;
  watchEnabled: boolean;
  postAction: PostAction;
  renameSuffix?: string | null;
  postActionSubfolder?: string | null;
  errorAction: ErrorAction;
  maxRetries: number;
  errorSubfolder?: string | null;
  allowedNamespaces: string[];
  processExistingOnStartup: boolean;
  pollingIntervalSeconds: number;
  fileStabilityCheck: boolean;
  fileStabilityTimeSeconds: number;
  fileStabilityTimeoutSeconds: number;
  postThroughTunnel: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

export type FileWatcherInput = Omit<FileWatcher, 'id' | 'hasPassword' | 'createdAt' | 'updatedAt'> & {
  /** Write-only: empty keeps the stored one. */
  password?: string;
  clearPassword?: boolean;
};

const query = (q: Record<string, string | number | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : '';
};

export type LogEvent =
  | { event: 'connected'; data: { clientId: string } }
  | { event: 'log'; data: AgentLogEntry }
  | { event: 'end'; data: { reason: string } };

export function automationGw(target: string) {
  const base = `/automation/${encodeURIComponent(target)}`;
  const client = targetHubClient(target);
  const agent = (clientId: string) => encodeURIComponent(clientId);
  return {
    tenants: () => client.get<Tenant[]>(`${base}/tenants`),
    createTenant: (name: string) => client.post<Tenant>(`${base}/tenants`, { name }),
    updateTenant: (id: string, body: { name: string; authTenantId: string | null }) =>
      client.put<Tenant>(`${base}/tenants/${id}`, body),
    deleteTenant: (id: string) => client.del<void>(`${base}/tenants/${id}`),

    agents: () => client.get<Agent[]>(`${base}/clients`),
    createAgent: (body: AgentInput) => client.post<Agent>(`${base}/clients`, body),
    updateAgent: (id: string, body: AgentInput) => client.put<Agent>(`${base}/clients/${id}`, body),
    deleteAgent: (id: string) => client.del<void>(`${base}/clients/${id}`),
    /** A new shared secret, shown once; the agent keeps failing until it is configured with it on site. */
    rotateSecret: (id: string) =>
      client.post<{ sharedSecret: string }>(`${base}/clients/${id}/rotate-secret`),
    /** Creates the agent's AUTH client and enrolls it over its socket (needs this environment's AUTH session). */
    moveToAuth: (id: string) => client.post<Agent>(`${base}/clients/${id}/move-to-auth`),
    setAuthMode: (id: string, mode: AuthMode) =>
      client.put<Agent>(`${base}/clients/${id}/auth-mode`, { mode }),
    /** For an agent in a container: creates its AUTH client and registers it; the secret is answered once, for the
     * container's `AGENT__AUTH__*` environment (nothing is pushed to the agent). */
    containerAuthClient: (id: string) =>
      client.post<ContainerAuthClient>(`${base}/clients/${id}/auth-client`),
    /** Forget the agent's AUTH client: back on its shared secret (`legacy`). */
    removeAuthClient: (id: string) =>
      client.put<Agent>(`${base}/clients/${id}/auth-client`, { authClientId: null }),

    routes: () => client.get<Route[]>(`${base}/routes`),
    createRoute: (body: RouteInput) => client.post<Route>(`${base}/routes`, body),
    updateRoute: (id: string, body: RouteInput) => client.put<Route>(`${base}/routes/${id}`, body),
    deleteRoute: (id: string) => client.del<void>(`${base}/routes/${id}`),

    fileWatchers: () => client.get<FileWatcher[]>(`${base}/file-watchers`),
    createFileWatcher: (body: FileWatcherInput) => client.post<FileWatcher>(`${base}/file-watchers`, body),
    updateFileWatcher: (id: string, body: FileWatcherInput) =>
      client.put<FileWatcher>(`${base}/file-watchers/${id}`, body),
    deleteFileWatcher: (id: string) => client.del<void>(`${base}/file-watchers/${id}`),

    tunnels: () => client.get<TunnelStatus[]>(`${base}/status/tunnels`),
    info: (clientId: string) => client.get<AgentInfo>(`${base}/status/tunnels/${agent(clientId)}/info`),
    history: (clientId: string, days = 30) =>
      client.get<ConnectionHistoryEntry[]>(
        `${base}/status/tunnels/${agent(clientId)}/history${query({ days })}`
      ),
    diagnostics: (clientId: string) =>
      client.post<{ message: string }>(`${base}/status/tunnels/${agent(clientId)}/diagnostics`),
    statistics: (hours: number) =>
      client.get<{ hours: number; routes: RouteStats[] }>(`${base}/status/statistics${query({ hours })}`),

    latest: () => client.get<UpdateInfo>(`${base}/updates/latest`),
    refreshUpdates: () => client.post<{ message: string }>(`${base}/updates/refresh-cache`),
    triggerUpdate: (clientId: string, body: { targetVersion?: string; force?: boolean }) =>
      client.post<TriggerUpdateResponse>(`${base}/updates/trigger/${agent(clientId)}`, body),
    cancelPendingUpdate: (clientId: string) => client.del<void>(`${base}/updates/pending/${agent(clientId)}`),
    restart: (clientId: string) =>
      client.post<{ status: string }>(`${base}/clients/${agent(clientId)}/restart`),

    logStatus: (clientId: string) => client.get<LogStatus>(`${base}/logs/status/${agent(clientId)}`),
    setDebug: (clientId: string, on: boolean) =>
      client.post<unknown>(`${base}/logs/debug/${on ? 'enable' : 'disable'}/${agent(clientId)}`),
    /** worker.log + bootstrapper.log from the agent's disk. */
    downloadLogs: async (clientId: string): Promise<{ blob: Blob; fileName: string }> => {
      const r = await client.requestRaw('GET', `${base}/logs/download/${agent(clientId)}`, undefined);
      const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
        r.headers.get('Content-Disposition') ?? ''
      );
      return {
        blob: await r.blob(),
        fileName: m ? decodeURIComponent(m[1] ?? m[2]) : `agent-logs-${clientId}.log`,
      };
    },
    /** The agent's live log until `signal` aborts or the agent goes away (`end`). */
    streamLogs: (clientId: string, onEvent: (e: LogEvent) => void, signal: AbortSignal) =>
      streamSse(
        client,
        'GET',
        `${base}/logs/stream/${agent(clientId)}`,
        undefined,
        (event, data) => onEvent({ event, data } as LogEvent),
        signal
      ),
  };
}

export type AutomationGwApi = ReturnType<typeof automationGw>;
